import { setActiveBoard } from 'modules/template-editor/store/templates/templates';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsHideControlPanel } from 'modules/overlay-v2/store/overlay/selectors';
import OverlayMessage from 'shared/components/overlay-message';
import useOnLoadedCarousel from 'modules/overlay-v2/ui/hooks/on-loaded/use-on-loaded-carousel';
import useOnLoadedCommon from 'modules/overlay-v2/ui/hooks/on-loaded/use-on-loaded-common';
import useOnLoadedTv from 'modules/overlay-v2/ui/hooks/on-loaded/use-on-loaded-tv';
import useBoardPresenter from 'modules/overlay-canvas/hooks/use-board-presenter';
import useFabric from 'modules/overlay-canvas/hooks/use-fabric';
import useFade from 'modules/overlay-canvas/hooks/use-fade';
import useReplacer from 'modules/overlay-canvas/hooks/use-replacer';
import useResizeCanvas from 'modules/overlay-canvas/hooks/use-resize-canvas';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { boardDiagnostics } from 'modules/overlay-v2/domain/utils/board-diagnostics';
import { millisToMinutesAndSeconds } from 'modules/overlay-v2/domain/utils/convert-time';
import { selectPeriodLabel } from 'modules/overlay-v2/store/game-info/selectors';
import useGameInfo from 'modules/overlay-v2/ui/hooks/use-game-info';
import useTimer from 'modules/overlay-v2/ui/hooks/use-timer';

import { CanvasWrapper } from './styles';

const NO_PLAYERS_MESSAGE_DURATION = 5000;

export function CanvasStaticBoards() {
  const { canvas, manager } = useFabric('static', { id: 'boards' });
  const dispatch = useDispatch();
  const replacer = useReplacer(manager);
  const { activeTemplate } = useTemplates();
  const { activeScheme, activeBoard } = useBoards();
  const { timer } = useTimer();
  const periodLabel = useSelector(selectPeriodLabel);
  const gameInfo = useGameInfo();
  const fade = useFade(manager);
  const presenter = useBoardPresenter(manager, fade);

  const [isInit, setIsInit] = useState(false);
  const [showNoPlayersMessage, setShowNoPlayersMessage] = useState(false);

  const hasDeclaredPlayers = useMemo(() => {
    const members = [...(gameInfo.teams?.first.members ?? []), ...(gameInfo.teams?.second.members ?? [])];
    return members.some((member) => member.isInvolved);
  }, [gameInfo.teams]);

  const timeout = useRef<ReturnType<typeof setInterval> | null>(null);
  const previousBoard = useRef<typeof activeBoard>(undefined);
  const isHideControlPanel = useSelector(selectIsHideControlPanel);

  useEffect(() => {
    if (!manager) return;
    boardDiagnostics.registerCanvas('boards', () => manager.canvas);
    return () => boardDiagnostics.unregisterCanvas('boards');
  }, [manager]);

  useResizeCanvas({ manager, isManagerInit: isInit, isHideControlPanel });
  const { onLoaded: onLoadedTV } = useOnLoadedTv({ manager });
  const { onLoaded: onLoadedCommon } = useOnLoadedCommon({ manager });
  const { onLoaded: onLoadedCarousel } = useOnLoadedCarousel({ manager, timeout });

  useEffect(() => {
    if (!manager) return;
    if (!activeTemplate) return;
    manager?.setResolution(activeTemplate.resolution);
    manager?.setDimensions(activeTemplate.resolution);
    manager.normalizedViewport();
    setIsInit(true);
  }, [activeTemplate, fade, manager]);

  useEffect(() => {
    if (!manager) return;
    timeout.current && clearInterval(timeout.current);
    setShowNoPlayersMessage(false);
    const allObject = manager.canvas.getObjects();

    // Замер (по умолчанию выключен): перезапуск показа при том же id табло означает, что
    // эффект дёрнула смена самого объекта табло, а не смена активного табло.
    boardDiagnostics.record('show:start', {
      id: activeBoard?.id ?? null,
      type: activeBoard?.type ?? null,
      isSameBoardNewObject: previousBoard.current !== activeBoard && previousBoard.current?.id === activeBoard?.id,
      objectsBefore: allObject.length,
    });
    previousBoard.current = activeBoard;

    // Показом владеет презентер: он же отменяет всё, что осталось от предыдущего табло —
    // затухание, загрузку схемы, её колбэки и поздние подстановки картинок.
    presenter.show({
      scheme: activeBoard ? activeScheme : undefined,
      clearOnEmpty: !activeBoard,
      onFaded: () => {
        boardDiagnostics.record('show:faded', { id: activeBoard?.id ?? null });
      },
      beforeLoad: (token) => {
        // Without declared players the carousel has nothing to show — display a
        // notice for a few seconds, then turn the board off.
        if (activeBoard?.type !== 'carousel' || hasDeclaredPlayers) return true;

        setShowNoPlayersMessage(true);
        timeout.current = setTimeout(() => {
          if (!token.isCurrent) return;
          setShowNoPlayersMessage(false);
          dispatch(setActiveBoard({ id: undefined }));
        }, NO_PLAYERS_MESSAGE_DURATION);
        return false;
      },
      onLoaded: () => {
        if (!activeBoard) return;
        boardDiagnostics.record('show:loaded', { id: activeBoard.id, type: activeBoard.type });

        if (activeBoard.type === 'carousel') {
          onLoadedCarousel();
          return;
        }

        onLoadedCommon();
        if (activeBoard.type === 'tv') {
          onLoadedTV();
        }
      },
    });

    // eslint-disable-next-line
  }, [activeBoard, manager, replacer]);

  useEffect(() => {
    if (!manager) return;
    replacer.updateText({ indicator: 'teamScore0', text: gameInfo.goals.first.toString() });
    replacer.updateText({ indicator: 'teamScore1', text: gameInfo.goals.second.toString() });
    replacer.updateText({ indicator: 'teamFouls0', text: gameInfo.fouls.first.toString() });
    replacer.updateText({ indicator: 'teamFouls1', text: gameInfo.fouls.second.toString() });

    replacer.updateText({ indicator: 'teamScore', text: gameInfo.goals.first.toString(), teamIndex: 0 });
    replacer.updateText({ indicator: 'teamScore', text: gameInfo.goals.second.toString(), teamIndex: 1 });
    replacer.updateText({ indicator: 'teamFouls', text: gameInfo.fouls.first.toString(), teamIndex: 0 });
    replacer.updateText({ indicator: 'teamFouls', text: gameInfo.fouls.second.toString(), teamIndex: 1 });
    replacer.updateTeamPeriodFouls({ indicator: 'teamPeriodFouls', options: { teamIndex: 0, count: gameInfo.fouls?.first ?? 0 } });
    replacer.updateTeamPeriodFouls({ indicator: 'teamPeriodFouls', options: { teamIndex: 1, count: gameInfo.fouls?.second ?? 0 } });
  }, [gameInfo.fouls, gameInfo.goals, manager, replacer]);

  useEffect(() => {
    if (!manager) return;
    const { shootoutGoals, shootoutMisses, shootoutAttempts } = gameInfo;

    replacer.updateText({ indicator: 'teamShootoutScore', text: shootoutGoals.first.toString(), teamIndex: 0 });
    replacer.updateText({ indicator: 'teamShootoutScore', text: shootoutGoals.second.toString(), teamIndex: 1 });
    replacer.updateText({ indicator: 'teamShootoutMissCount', text: shootoutMisses.first.toString(), teamIndex: 0 });
    replacer.updateText({ indicator: 'teamShootoutMissCount', text: shootoutMisses.second.toString(), teamIndex: 1 });
    replacer.updateShootoutSeries({ indicator: 'teamShootoutSeries', options: { teamIndex: 0, attempts: shootoutAttempts.first } });
    replacer.updateShootoutSeries({ indicator: 'teamShootoutSeries', options: { teamIndex: 1, attempts: shootoutAttempts.second } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameInfo.shootoutAttempts, manager, replacer]);

  useEffect(() => {
    if (!manager) return;
    replacer.updateText({ indicator: 'matchTime', text: millisToMinutesAndSeconds(timer) });
    replacer.updateText({ indicator: 'period', text: periodLabel });
  }, [replacer, timer, periodLabel, manager]);

  return (
    <>
      <CanvasWrapper>{canvas}</CanvasWrapper>
      <OverlayMessage
        isOpen={showNoPlayersMessage}
        title={'Нет заявленных игроков на матч'}
        subtitle={'Невозможно отобразить карусель'}
        style={{ position: 'absolute', inset: 0, height: '100%', zIndex: 90 }}
      />
    </>
  );
}
